import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import type {
  JobBoardCredential,
  JobBoardCredentialCreateParams,
  JobBoardCredentialUpdateParams,
} from '@recruitee/job-boards-types';

import { JobBoardCredentialHttpClient } from './credential-http.client';
import { JobBoardCredentialActions, JobBoardCredentialSelectors } from './credential.store';

@Injectable({ providedIn: 'root' })
export class JobBoardCredentialStoreService {
  private readonly store = inject(Store);
  private readonly jobBoardCredentialHttpClient = inject(JobBoardCredentialHttpClient);

  public getJobBoardCredentials(codename: string): Observable<JobBoardCredential[]> {
    return this.store
      .select(JobBoardCredentialSelectors.entities({ scope: codename }))
      .pipe(filterBoolean);
  }

  public getJobBoardCredentialsLoaded(codename: string): Observable<boolean> {
    return this.store.select(JobBoardCredentialSelectors.isLoaded({ scope: codename }));
  }

  public fetchJobBoardCredentials(codename: string): void {
    this.store.dispatch(
      JobBoardCredentialActions.fetchCollection({ payload: { scope: codename } }),
    );
  }

  public createCredential(
    codename: string,
    params: JobBoardCredentialCreateParams,
  ): Observable<JobBoardCredential> {
    return this.jobBoardCredentialHttpClient.createCredential(codename, params).pipe(
      tap(credential => {
        this.fetchJobBoardCredentials(codename);
        this.store.dispatch(
          JobBoardCredentialActions.credentialCreated({
            payload: { context: { codename, credential } },
          }),
        );
      }),
    );
  }

  public updateCredential(
    codename: string,
    credentialId: number,
    params: JobBoardCredentialUpdateParams,
  ): Observable<JobBoardCredential> {
    return this.jobBoardCredentialHttpClient.updateCredential(codename, credentialId, params).pipe(
      tap(credential => {
        this.fetchJobBoardCredentials(codename);
        this.store.dispatch(
          JobBoardCredentialActions.credentialUpdated({
            payload: { context: { codename, credential } },
          }),
        );
      }),
    );
  }

  public deleteCredential(codename: string, credentialId: number): Observable<JobBoardCredential> {
    return this.jobBoardCredentialHttpClient.deleteCredential(codename, credentialId).pipe(
      tap(credential => {
        this.fetchJobBoardCredentials(codename);
        this.store.dispatch(
          JobBoardCredentialActions.credentialDeleted({
            payload: { context: { codename, credential } },
          }),
        );
      }),
    );
  }
}
